/**
 * Curated Font Awesome 6 Sharp icon choices — THE ONE shared list behind both
 * icon fields of the Linkview feature (Anke, 2026-09-04 for the tiles,
 * extended 2026-09-20 to the boards): the dashboard-creator's "icon above
 * text" TILE field (`ha-dialog-link-tile-edit`) and the BOARD icon picked
 * when a Linkboard is created (`ha-dialog-linkview-create`). Both render it
 * through the same `ha-input-icon` component, so a tile and a board offer
 * exactly the same 23 icons.
 *
 * The name `LINK_TILE_ICONS` (and the file name) is historic — it predates
 * the board picker and was deliberately NOT renamed, because the constant is
 * referenced from the dialogs, the sidebar-icon helper and several test
 * suites; the list is the shared one regardless of what it is called.
 *
 * `codepoint` is the hex Unicode PUA value the self-hosted
 * `fa-sharp-light-300.woff2` subset maps that glyph to — verified against the
 * actual font file's cmap (not guessed; all 23 re-verified 2026-09-20), same
 * codepoints already used elsewhere in this app for the same icons (e.g.
 * `link` = f0c1, see todo_sidebar.css's ha-mode-switch rule).
 *
 * A small hand-picked set rather than the full ~2000-icon library: there is
 * no icon-search input in this codebase, so an exhaustive list would be
 * unusable as a plain grid. `ha-input-icon` renders the actual character
 * (`String.fromCodePoint`) styled with the icon font-family — not a static
 * per-selector `::before` rule (the house convention for a FIXED, single
 * icon usage) — because this is a data-driven list where any entry can end
 * up in any tile at runtime.
 *
 * THE SIDEBAR IS THE ONE EXCEPTION: `linkview-sidebar-list` keeps drawing a
 * board's icon through `data-icon="<key>"` + a `::before` rule per key in
 * `backend/public/assets/css/linkview_sidebar.css` (see `linkview-icons.helper.ts`).
 * `tests/domain/helpers/linkview-icons.helper.test.ts` checks that every `id`
 * here has such a rule and that its `content` is exactly this `codepoint`, so
 * the list and the stylesheet cannot drift apart.
 *
 * The ORDER is the order both pickers render them in.
 */
export interface LinkTileIcon {
    readonly id: string;
    readonly codepoint: string;
    readonly label: string;
}

export const LINK_TILE_ICONS: readonly LinkTileIcon[] = [
    {id: 'link', codepoint: 'f0c1', label: 'Link'},
    {id: 'house', codepoint: 'f015', label: 'Haus'},
    {id: 'star', codepoint: 'f005', label: 'Stern'},
    {id: 'bookmark', codepoint: 'f02e', label: 'Lesezeichen'},
    {id: 'folder', codepoint: 'f07b', label: 'Ordner'},
    {id: 'envelope', codepoint: 'f0e0', label: 'E-Mail'},
    {id: 'calendar', codepoint: 'f133', label: 'Kalender'},
    {id: 'chart-line', codepoint: 'f201', label: 'Diagramm'},
    {id: 'globe', codepoint: 'f0ac', label: 'Globus'},
    {id: 'book', codepoint: 'f02d', label: 'Buch'},
    {id: 'camera', codepoint: 'f030', label: 'Kamera'},
    {id: 'cloud', codepoint: 'f0c2', label: 'Cloud'},
    {id: 'heart', codepoint: 'f004', label: 'Herz'},
    {id: 'flag', codepoint: 'f024', label: 'Flagge'},
    {id: 'image', codepoint: 'f03e', label: 'Bild'},
    {id: 'briefcase', codepoint: 'f0b1', label: 'Aktentasche'},
    // Added 2026-09-20 with the board picker: the icons Anke's own board list
    // had and the tile list did not. Tiles now offer them too (intentional —
    // one shared list, see the doc comment above).
    {id: 'bell', codepoint: 'f0f3', label: 'Glocke'},
    {id: 'calendar-days', codepoint: 'f073', label: 'Kalender (Tage)'},
    {id: 'users', codepoint: 'f0c0', label: 'Personen'},
    {id: 'gear', codepoint: 'f013', label: 'Zahnrad'},
    {id: 'wrench', codepoint: 'f0ad', label: 'Schraubenschlüssel'},
    {id: 'lightbulb', codepoint: 'f0eb', label: 'Glühbirne'},
    {id: 'rocket', codepoint: 'f135', label: 'Rakete'},
];
